/* eslint-disable @typescript-eslint/camelcase */
import React, { useState, useEffect, FC, useContext } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import "../styles/PageNursingHome.scss";
import config from "./config";
import axios from "axios";
import {
	GetNursingHomeResponse,
	NursingHome,
	NursingHomeImageName,
} from "./types";
import { MapSmall } from "./Map";
import { useT } from "../i18n";
import Lightbox from "./Lightbox";
import Title from "./Title";
import VacancyStatusBadge from "./VacancyStatusBadge";
import DefinitionItem from "./DefinitionItem";
import ParagraphLink from "./ParagraphLink";
import { HeaderContext, HeaderStyle } from "./header-context";

interface ParagraphProps {
	title?: string;
	text?: string;
	className?: string;
}

const Paragraph: FC<ParagraphProps> = ({ title, text, className }) => {
	if (!text) return null;

	return (
		<>
			{title && (
				<p className="nursinghome-info-paragraph-title">{title}</p>
			)}
			<p className={className}>{text}</p>
		</>
	);
};

interface ImageProps {
	nursingHome: NursingHome | null;
	imageName: NursingHomeImageName | null | undefined;
	className?: string;
	alt?: string;
	placeholder?: JSX.Element;
	variant?: "img" | "background";
	onClick?: () => void;
}

export const Image: FC<ImageProps> = ({
	nursingHome,
	imageName,
	className,
	alt,
	placeholder = null,
	variant = "img",
	onClick,
}) => {
	if (!imageName || !nursingHome || !nursingHome.pic_digests) {
		return placeholder;
	}

	const digest: string = (nursingHome.pic_digests as any)[
		`${imageName}_hash`
	];

	if (!digest) {
		return placeholder;
	}

	const srcUrl = `${config.API_URL}/nursing-homes/${nursingHome.id}/pics/${imageName}/${digest}`;

	if (variant === "img") {
		return (
			<img
				src={srcUrl}
				className={className}
				alt={alt}
				onClick={onClick}
			/>
		);
	} else {
		return (
			<div className={className} onClick={onClick}>
				<div
					className="nursinghome-img-inner"
					style={{
						backgroundImage: `url(${srcUrl})`,
					}}
				/>
			</div>
		);
	}
};

interface NursingHomeDetailsBoxProps {
	nursingHome: NursingHome;
	className?: string;
	id?: string;
}

const NursingHomeDetailsBox: FC<NursingHomeDetailsBoxProps> = ({
	nursingHome,
	className,
	id,
}) => {
	const contactInfo = useT("contactInfo");
	const directions = useT("directions");
	const webpage = useT("webpage");
	const visitingInfo = useT("visitingInfo");
	const openReport = useT("openReport");
	const reportScoreHeader = useT("reportScoreHeader");
	const giveReview = useT("giveReview");
	const readMore = useT("readMore");
	const feedbackCustomerReview = useT("feedbackCustomerReview");
	const feedbackRelativeReview = useT("feedbackRelativeReview");
	const feedbackNoReviews = useT("feedbackNoReviews");
	const feedbackAverage = useT("feedbackAverage");

	const surveyOption1 = useT("surveyOption1");
	const surveyOption2 = useT("surveyOption2");
	const surveyOption3 = useT("surveyOption3");
	const surveyOption4 = useT("surveyOption4");
	const surveyOption5 = useT("surveyOption5");

	const reportStatusOk = useT("status_ok_long");
	const reportStatusSmall = useT("status_small_issues_long");
	const reportStatusSignificant = useT("status_significant_issues_long");
	const reportStatusSurveillance = useT("status_surveillance_long");
	const reportStatusNoInfo = useT("status_no_info");

	const reportTypeAnnounced = useT("reportTypeAnnounced");
	const reportTypeUnannounced = useT("reportTypeUnannounced");
	const reportTypeConcern = useT("reportTypeConcern");
	const linkReadMoreSurveillance = useT("linkReadMoreSurveillance");

	let reportStatus = useT("status_waiting");

	let hasReport = false;

	const formatDate = (dateStr: string | null): string => {
		if (!dateStr) {
			return "";
		}

		const date = new Date(dateStr);

		const YYYY = String(date.getUTCFullYear());
		const MM = String(date.getUTCMonth() + 1);
		const DD = String(date.getUTCDate());

		return `${DD}.${MM}.${YYYY}`;
	};

	const ratingToString = (
		answers: number | null,
		rating: number | null,
	): string => {
		if (rating && answers) {
			if (answers >= 5) {
				if (rating > 4.5) {
					return surveyOption5;
				} else if (rating > 3.5) {
					return surveyOption4;
				} else if (rating > 2.5) {
					return surveyOption3;
				} else if (rating > 1.5) {
					return surveyOption2;
				} else if (rating > 0.5) {
					return surveyOption1;
				}
			}

			return feedbackAverage;
		}

		return " ";
	};

	const getTypeTranslation = (typeStr: string): string => {
		if (nursingHome) {
			switch (typeStr) {
				case "announced":
					return reportTypeAnnounced;
				case "audit":
					return reportTypeUnannounced;
				case "concern":
					return reportTypeConcern;
			}
		}
		return "";
	};

	if (nursingHome.report_status) {
		switch (nursingHome.report_status[0].status) {
			case "ok":
				reportStatus = reportStatusOk;
				hasReport = true;
				break;
			case "small":
				reportStatus = reportStatusSmall;
				hasReport = true;
				break;
			case "significant":
				reportStatus = reportStatusSignificant;
				hasReport = true;
				break;
			case "surveillance":
				reportStatus = reportStatusSurveillance;
				hasReport = true;
				break;
			case "no-info":
				reportStatus = reportStatusNoInfo;
				break;
		}
	}

	const reports: JSX.Element[] | null =
		nursingHome.report_status &&
		nursingHome.report_status.map((status, index) => (
			<div className={hasReport ? "" : "report-hidden"} key={index}>
				<p className={"report-info-item"}>
					{getTypeTranslation(status.type)} {formatDate(status.date)}
				</p>

				<a
					href={`/api/nursing-homes/${nursingHome.id}
						/raportti/${index}/Valvontaraportti-${nursingHome.owner.replace(/\/|\\/gi, "-")}
						-${nursingHome.name.replace(/\/|\\/gi, "-")}
						-${formatDate(status.date).replace(/\/|\\/gi, "-")}.pdf`}
					target="_blank"
					rel="noopener noreferrer"
					className="btn-secondary-link"
				>
					{openReport}
				</a>
			</div>
		));

	const nursingHomeRating = nursingHome.rating;
	const hasRating = !!nursingHomeRating;

	const enoughCustomerAnswers =
		hasRating &&
		nursingHomeRating.answers_customers &&
		nursingHomeRating.answers_customers >= 5;

	const enoughRelativesAnswers =
		hasRating &&
		nursingHomeRating.answers_relatives &&
		nursingHomeRating.answers_relatives >= 5;

	return (
		<>
			{id && <div id={id} />}
			<div className={className}>
				{
					<Link
						to={`/hoivakodit/${nursingHome.id}/anna-arvio`}
						className="nursinghome-details-box-survey-link"
					>
						<button className="btn report-info-btn">
							{giveReview}
						</button>
					</Link>
				}
				<div className="nursinghome-details-box-section">
					<Image
						nursingHome={nursingHome}
						imageName="owner_logo"
						className="nursinghome-details-logo"
						alt="Omistajan logo"
					/>
					<h4 className="nursinghome-details-heading">
						{nursingHome.name}
					</h4>
					<a
						href={`https://www.google.com/maps/search/${
							nursingHome.name
						}/@${nursingHome.geolocation.center.join(",")}z`}
						target="_blank"
						rel="noreferrer noopener external"
						className="mapLink"
					>
						<MapSmall nursingHome={nursingHome} />
					</a>

					<dl className="nursinghome-info-list nursinghome-info-list--contact">
						<dt>{contactInfo}</dt>
						<dd>
							{nursingHome.address}, {nursingHome.postal_code}{" "}
							{nursingHome.city}
						</dd>
						<dd>
							<a
								href={nursingHome.www}
								target="_blank"
								rel="noopener noreferrer external"
							>
								{webpage}
							</a>
						</dd>
						<dd style={{ marginTop: 8 }}>
							<a href="#visitingInfo"> {visitingInfo}</a>
						</dd>
					</dl>

					<dl className="nursinghome-info-list nursinghome-info-list--directions">
						<dt>{directions}</dt>
						<dd>{nursingHome.arrival_guide_public_transit}</dd>
						<dd>{nursingHome.arrival_guide_car}</dd>
					</dl>
				</div>
				<div className="nursinghome-details-box-section">
					<div className="report-info-container">
						<div>
							<p>{feedbackCustomerReview}</p>
							<p>
								<span className="report-info-minor-header">
									{nursingHomeRating &&
									nursingHomeRating.answers_customers
										? ratingToString(
												nursingHomeRating.answers_customers,
												nursingHomeRating.average_customers,
										  )
										: feedbackNoReviews}
								</span>{" "}
								{enoughCustomerAnswers &&
								nursingHomeRating.average_customers
									? `${nursingHomeRating.average_customers.toPrecision(
											2,
									  )} / 5`
									: ""}
							</p>
						</div>
						<div>
							<p>{feedbackRelativeReview}</p>
							<p>
								<span className="report-info-minor-header">
									{nursingHomeRating &&
									nursingHomeRating.average_relatives
										? ratingToString(
												nursingHomeRating.answers_relatives,
												nursingHomeRating.average_relatives,
										  )
										: feedbackNoReviews}
								</span>{" "}
								{enoughRelativesAnswers &&
								nursingHomeRating.average_relatives
									? `${nursingHomeRating.average_relatives.toPrecision(
											2,
									  )} / 5`
									: ""}
							</p>
						</div>
						<Link
							to={`/hoivakodit/${nursingHome.id}/arviot`}
							className={
								nursingHomeRating &&
								(nursingHomeRating.average_relatives ||
									nursingHomeRating.average_customers)
									? ""
									: "hidden"
							}
						>
							<button className="btn report-info-btn">
								{readMore}
							</button>
						</Link>
					</div>
				</div>
			</div>
		</>
	);
};

const getAvailablePics = (
	nursingHome: NursingHome,
): [string, string][] | null => {
	const { pic_digests = [] }: { pic_digests: any } = nursingHome;

	const availablePicHashes =
		nursingHome &&
		nursingHome.pic_digests &&
		Object.keys(nursingHome.pic_digests)
			.filter(name => pic_digests[name] !== null)
			.filter(hashName => hashName !== "owner_logo_hash");

	if (!availablePicHashes || availablePicHashes.length === 0) {
		return null;
	}

	const availablePics = availablePicHashes.map<[string, string]>(hashName => {
		const imageName = hashName.replace("_hash", "");

		const digest: string = pic_digests[hashName];

		return [imageName, digest];
	});

	return availablePics;
};

interface NursingHomeRouteParams {
	id: string;
}

const PageNursingHome: FC = () => {
	const { id } = useParams<NursingHomeRouteParams>();

	const location = useLocation();

	const [nursingHome, setNursingHome] = useState<NursingHome | null>(null);
	const [picCaptions, setPicCaptions] = useState<Record<
		string,
		string
	> | null>(null);
	const [lightboxState, setLightboxState] = useState<"hidden" | number>(
		"hidden",
	);
	const { setHeaderStyle } = useContext(HeaderContext);

	useEffect(() => {
		setHeaderStyle(HeaderStyle.BLUE);

		return () => {
			setHeaderStyle(null);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		axios
			.get(`${config.API_URL}/nursing-homes/${id}`)
			.then((response: GetNursingHomeResponse) => {
				setNursingHome(response.data);
			})
			.catch(e => {
				console.error(e);
				throw e;
			});
	}, [id]);

	useEffect(() => {
		axios
			.get(`${config.API_URL}/nursing-homes/${id}/pics/captions`)
			.then((response: { data: Record<string, string> }) => {
				setPicCaptions(response.data);
			})
			.catch(e => {
				console.error(e);
				throw e;
			});
	}, [id]);

	const linkBacktoList = useT("linkBacktoList");
	const anchorDetailsBox = useT("anchorDetailsBox");
	const loadingText = useT("loadingText");
	const filterAraLabel = useT("filterAraLabel");
	const numApartments = useT("numApartments");
	const buildingInfo = useT("buildingInfo");
	const apartmentCountInfo = useT("apartmentCountInfo");
	const serviceLanguage = useT("serviceLanguage");
	const language = useT(nursingHome && (nursingHome.language as any));
	const languageInfo = useT("languageInfo");
	const linkBacktoTop = useT("linkBacktoTop");
	const basicInformation = useT("basicInformation");
	const owner = useT("owner");
	const yearofConst = useT("yearofConst");
	const apartmentSize = useT("apartmentSize");
	const apartmentFurnitureLabel = useT("apartmentFurnitureLabel");
	const apartmentFurnitureText = useT("apartmentFurnitureText");
	const rent = useT("rent");
	const checkRentWithNursingHome = useT("checkRentWithNursingHome");
	const LAHapartments = useT("LAHapartments");
	const foodHeader = useT("foodHeader");
	const cookingMethod = useT("cookingMethod");
	const foodMoreInfo = useT("foodMoreInfo");
	const linkMenu = useT("linkMenu");
	const activies = useT("activies");
	const outdoorActivies = useT("outdoorActivies");
	const visitingInfo = useT("visitingInfo");
	const accessibility = useT("accessibility");
	const personnel = useT("personnel");
	const otherServices = useT("otherServices");
	const nearbyServices = useT("nearbyServices");
	const monthShort = useT("monthShort");
	const btnShowImages = useT("btnShowImages");
	const linkMoreInfoOutdoor = useT("linkMoreInfoOutdoor");
	const linkMoreInfoActivies = useT("linkMoreInfoActivies");
	const linkMoreInfoPersonnel = useT("linkMoreInfoPersonnel");
	const filterYes = useT("filterYes");
	const filterNo = useT("filterNo");
	const filterARABoth = useT("filterARABoth");

	const availablePics = nursingHome && getAvailablePics(nursingHome);

	const images =
		nursingHome &&
		availablePics &&
		availablePics.map(([imageName, digest]) => {
			return {
				src:
					`${config.API_URL}/nursing-homes/${nursingHome.id}` +
					`/pics/${imageName}/${digest}`,
				caption:
					(picCaptions && picCaptions[`${imageName}_caption`]) ||
					null,
			};
		});

	const numPics = availablePics
		? availablePics.length >= 5
			? 5
			: availablePics.length >= 3
			? 3
			: 1
		: 0;

	const heroPics =
		nursingHome &&
		picCaptions &&
		availablePics &&
		availablePics.length &&
		availablePics.slice(0, numPics).map(([name]) => {
			return {
				name: name,
				caption:
					(picCaptions && picCaptions[`${name}_caption`]) || null,
			};
		});

	return (
		<div className="nursinghome-page-container">
			<Title title={nursingHome ? nursingHome.name : undefined} />
			{images && (
				<Lightbox
					state={lightboxState}
					onClose={() => setLightboxState("hidden")}
					images={images}
				/>
			)}
			<div className="nursinghome-hero-container">
				<div
					className={`nursinghome-hero nursinghome-hero-n${numPics}`}
				>
					{heroPics ? (
						heroPics.map((image, idx) => (
							<Image
								key={image.name}
								nursingHome={nursingHome}
								imageName={image.name as NursingHomeImageName}
								className="nursinghome-hero-img"
								variant="img"
								alt={(image.caption as string) || ""}
								placeholder={
									<div
										className={
											"nursinghome-hero-img " +
											"nursinghome-hero-placeholder"
										}
									/>
								}
								onClick={() => setLightboxState(idx)}
							/>
						))
					) : (
						<div className="nursinghome-hero-placeholder" />
					)}
				</div>
				{images && (
					<button
						onClick={() => setLightboxState(0)}
						className="nursinghome-hero-lightbox-button"
					>
						{btnShowImages}
					</button>
				)}
			</div>
			{!nursingHome ? (
				loadingText
			) : (
				<div className="nursinghome-info-container">
					<div className="nursinghome-info">
						<Link
							to={
								("/hoivakodit" +
									(location.state
										? location.state.fromFilterQuery
										: "")) as string
							}
							className="nursinghome-back-link"
						>
							{linkBacktoList}
						</Link>
						<h2 className="nursinghome-title">
							{nursingHome.name}
						</h2>

						<div className="nursinghomeDistrict-container">
							<Paragraph
								className="nursinghomeDistrict"
								text={`${
									nursingHome.district != null
										? nursingHome.district + ", "
										: ""
								} ${nursingHome.city}`}
							/>

							<a
								className="nursinghome-anchor-details"
								href="#yhteystiedot"
							>
								{anchorDetailsBox}
							</a>
						</div>

						<VacancyStatusBadge
							vacancyStatus={nursingHome.has_vacancy}
							className="nursinghome-title-vacancy-status-badge"
						/>

						<Paragraph text={nursingHome.summary} />

						<h3>{basicInformation}</h3>
						<dl className="nursinghome-info-list">
							<DefinitionItem
								term={owner}
								definition={nursingHome.owner}
							/>
							<DefinitionItem
								term={filterAraLabel}
								definition={
									nursingHome.ara === "Kyllä"
										? filterYes
										: nursingHome.ara === "Ei"
										? filterNo
										: filterARABoth
								}
							/>
							<DefinitionItem
								term={yearofConst}
								definition={String(
									nursingHome.construction_year,
								)}
							/>
							<DefinitionItem
								term={buildingInfo}
								definition={nursingHome.building_info}
							/>
							<DefinitionItem
								term={numApartments}
								definition={`${nursingHome.apartment_count}`}
							/>
							<DefinitionItem
								term={apartmentCountInfo}
								definition={nursingHome.apartment_count_info}
							/>
							<DefinitionItem
								term={apartmentSize}
								definition={`${nursingHome.apartment_square_meters} m²`}
							/>
							<DefinitionItem
								term={apartmentFurnitureLabel}
								definition={apartmentFurnitureText}
							/>
							<DefinitionItem
								term={rent}
								definition={`${checkRentWithNursingHome} ${nursingHome.rent}€ / ${monthShort}`}
							/>

							<DefinitionItem
								term={serviceLanguage}
								definition={language}
							/>
							<DefinitionItem
								term={languageInfo}
								definition={nursingHome.language_info}
							/>
							<DefinitionItem
								term={LAHapartments}
								definition={
									nursingHome.lah ? filterYes : filterNo
								}
							/>
						</dl>
						<h3>{foodHeader}</h3>
						<dl className="nursinghome-info-list">
							<DefinitionItem
								term={cookingMethod}
								definition={nursingHome.meals_preparation}
							/>
							<DefinitionItem
								term={foodMoreInfo}
								definition={nursingHome.meals_info}
							/>
						</dl>
						<ParagraphLink
							text={linkMenu}
							to={nursingHome.menu_link}
						/>
						<h3>{activies}</h3>
						<Paragraph text={nursingHome.activities_info} />
						<ParagraphLink
							text={linkMoreInfoActivies}
							to={nursingHome.activities_link}
						/>
						<h3>{outdoorActivies}</h3>
						<Paragraph
							text={nursingHome.outdoors_possibilities_info}
						/>
						{nursingHome.outdoors_possibilities_link && (
							<>
								<a
									href={
										nursingHome.outdoors_possibilities_link
									}
									target="_blank"
									rel="noreferrer noopener external"
								>
									{linkMoreInfoOutdoor}
								</a>
							</>
						)}

						<h3 id="visitingInfo">{visitingInfo}</h3>
						<Paragraph text={nursingHome.tour_info} />
						<dl className="nursinghome-info-list nursinghome-info-list--contact">
							<dt>{nursingHome.contact_name}</dt>
							<dd>{nursingHome.contact_title}</dd>
							<dd>Puh. {nursingHome.contact_phone}</dd>
							<dd>
								<a href={"mailto:" + nursingHome.email}>
									{nursingHome.email}
								</a>
							</dd>
							<dd>
								<br />
							</dd>
							<dd>{nursingHome.contact_phone_info}</dd>
						</dl>
						<h3>{accessibility}</h3>
						<Paragraph text={nursingHome.accessibility_info} />
						<h3>{personnel}</h3>
						<Paragraph text={nursingHome.staff_info} />

						{nursingHome.staff_satisfaction_info && (
							<>
								<a
									href={nursingHome.staff_satisfaction_info}
									target="_blank"
									rel="noreferrer noopener external"
								>
									{linkMoreInfoPersonnel}
								</a>
							</>
						)}

						{nursingHome.other_services && (
							<>
								<h3>{otherServices}</h3>
								<Paragraph text={nursingHome.other_services} />
							</>
						)}
						<h3>{nearbyServices}</h3>
						<Paragraph text={nursingHome.nearby_services} />
					</div>

					<NursingHomeDetailsBox
						nursingHome={nursingHome}
						id="yhteystiedot"
						className="nursinghome-details-box"
					/>
				</div>
			)}
			<a className="backToTopLink" href="#pageTop">
				{linkBacktoTop}
			</a>
		</div>
	);
};

export default PageNursingHome;
